<template>
    <div class="event-summary">
        <div class="event-summary__match-indicator-wrapper">
            <template v-if="isLive">
                <span class="event-summary__match-indicator-live">
                    <span class="event-summary__match-indicator-live-dot"></span>
                    <span
                        class="event-summary__match-indicator-live-text upper-case e2e-event-live-indicator"
                    >{{ $t('live') }}</span>
                </span>
                <span
                    v-if="liveData"
                    class="event-summary__match-indicator-period"
                >
                    {{ `${liveData.minute ? liveData.minute + "'" : '' }
                     ${liveData.stoppageTime
                    ? '+' + liveData.stoppageTime + "'"
                    : currentPeriodName }` }}
                </span>
                <span
                    v-else-if="startDate"
                    class="event-summary__match-indicator-period"
                >
                    {{ $formatTime(startDate) }}
                </span>
            </template>
            <template v-else>
                <span class="event-summary__match-indicator-day">
                    {{ $formatDateTime(startDate, dateFormat(startDate, isInGrid)) }}
                </span>
                <span class="event-summary__match-indicator-time">{{ $formatTime(startDate) }}</span>
            </template>
        </div>
        <div class="event-summary__competitors-and-scores">
            <div
                :class="{ 'event-summary__competitors-wrapper--two-teams': team1Name && team2Name }"
                class="event-summary__competitors-wrapper"
            >
                <span
                    class="event-summary__competitors-team1 e2e-event-team1-name"
                    :title="team1Name"
                >
                    <span
                        v-if="team1PreviousResult"
                        :title="$capitalize($t('previuos match result').toString())"
                        class="event-summary__competitors-past-result"
                    >
                        {{ team1PreviousResult }}
                    </span>
                    {{ team1Name }}
                    {{ teamCards(liveData, 1) }}
                </span>
                <span
                    v-if="team2Name"
                    :title="team2Name"
                    class="event-summary__competitors-team2 e2e-event-team2-name"
                >
                    <span
                        v-if="team2PreviousResult"
                        :title="$capitalize($t('previuos match result').toString())"
                        class="event-summary__competitors-past-result"
                    >
                        {{ team2PreviousResult }}
                    </span>
                    {{ team2Name }}
                    {{ teamCards(liveData, 2) }}
                </span>
            </div>
            <div class="event-summary__score-wrapper">
                <component
                    v-if="isLive && liveData"
                    :is="components.ScoreWidget"
                    :rawLiveData="liveData"
                    :sportId="sportId"
                    :useCompactLiveData="useCompactLiveData"
                    :periods="periods"
                />
                <div
                    v-else-if="result"
                    class="event-summary__score--simple"
                >
                    <div>{{ result.team1Score }}</div>
                    <div>{{ result.team2Score }}</div>
                </div>
                <template v-else>
                    <div
                        :class="{ 'event-summary__shop-active' : isCodeShown }"
                        class="event-summary__score--will-be-live"
                    >
                        <span
                            v-if="isCodeShown"
                            class="event-summary__shop-code"
                        >
                            {{ code }}
                        </span>
                        <Icon
                            v-else
                            v-tooltip="cap($t('live betting'))"
                            size="sm"
                            :class="viewingOptions && viewingOptions.liveBetting
                                ? 'icon-live_indicator icon' : ''"
                        />
                    </div>
                </template>
            </div>
        </div>
        <component
            v-if="viewingOptions"
            :is="components.EventMediaWidget"
            v-bind="viewingOptions"
        />
    </div>
</template>

<script>
    import { getConfig } from '@superbet-group/web.lib.config';
    import {
        capitalize,
        differenceInCalendarDays,
    } from '@superbet-group/web.lib.utilities';

    import EventMediaWidget from './EventMediaWidget.vue';
    import ScoreWidget from './ScoreWidget';
    import { getCurrentPeriodTranslation } from './helpers';
    import { featureFlagService } from '@superbet-group/web.lib.flags';

    export default {
        props: {
            team1Name: {
                type: String,
                required: true,
            },
            team2Name: {
                type: String,
                required: false,
                default: '',
            },
            team1PreviousResult: {
                type: String,
                required: false,
                default: '',
            },
            team2PreviousResult: {
                type: String,
                required: false,
                default: '',
            },
            viewingOptions: {
                type: Object,
                required: false,
                default: null,
            },
            isLive: {
                type: Boolean,
                required: true,
            },
            startDate: {
                type: Date,
                required: true,
            },
            liveData: {
                type: Object,
                required: false,
                default: null,
            },
            result: {
                type: Object,
                default: null,
            },
            sportId: {
                type: Number,
                required: false,
                default: undefined,
            },
            useCompactLiveData: {
                type: Boolean,
                default: false,
            },
            isInGrid: {
                type: Boolean,
                required: false,
                default: false,
            },
            isCodeShown: {
                type: Boolean,
                required: false,
                default: false,
            },
            code: {
                type: [Number, String],
                required: false,
                default: '',
            },
            cap: {
                type: Function,
                required: false,
                default: capitalize,
            },
            dateFormat: {
                type: Function,
                required: false,
                default: (date, isInGrid) => {
                    const now = new Date();
                    const isInFuture = differenceInCalendarDays(now, date)
                        > getConfig().app.sportOffer.gridDateFutureThreshold;

                    if (isInFuture) {
                        return 'ddmm';
                    }

                    return isInGrid ? 'w' : 'wdd';
                },
            },
            periods: {
                type: Array,
                required: false,
                default: undefined,
            },
            components: {
                type: Object,
                default() {
                    return {
                        EventMediaWidget,
                        ScoreWidget,
                    };
                },
            },
            teamCards: {
                type: Function,
                required: false,
                default(liveData, index) {
                    const redCardProp = `t${index}RCrd`;
                    const yellowCardProp = `t${index}YCrd`;
                    if (liveData?.[redCardProp] && liveData?.[yellowCardProp]) {
                        return 'red-yellow-card';
                    } else if (liveData?.[redCardProp]) {
                        return 'red-card';
                    } else {
                        return '';
                    }
                },
            },
        },
        data() {
            return {
                translatePeriodNames: false,
            };
        },
        created() {
            this.featureSubscriptionUnsubscribeFn = featureFlagService.subscribeToFeatureChanges(
                'sportOffer.translate-live-periods',
                (isEnabled) => this.translatePeriodNames = isEnabled,
                false)
            ;
        },
        computed: {
            currentPeriodName() {
                if (!this.translatePeriodNames) {
                    return this.liveData.periodStatus;
                }
                const translation = getCurrentPeriodTranslation(this.liveData);
                if (translation) {
                    return `${translation.period ?? ''}${this.$t(translation.key)}`;
                } else {
                    return this.liveData.periodStatus;
                }
            },
        },
        destroyed() {
            this.featureSubscriptionUnsubscribeFn?.();
        },
    };
</script>
